import { type HTMLAttributeAnchorTarget, type PropsWithChildren } from 'react';
import cn from 'classnames';
import {
    IconOpenInNewTab,
    type IconSize,
    Link,
    type LinkColor,
    type PressEvent,
    type TestProps,
    Text,
    type TextSize,
} from '@direct-frontend/components';
import { NON_BREAKING_SPACE } from '../../lib/constants';
import styles from './LinkWithIcon.module.css';

interface Props extends PropsWithChildren, TestProps {
    className?: string;
    href?: string;
    external?: boolean;
    target?: HTMLAttributeAnchorTarget;
    icon?: typeof IconOpenInNewTab;
    inline?: boolean;
    iconSize?: IconSize;
    size?: Extract<TextSize, 'xs' | 's' | 'm'>;
    color?: LinkColor;
    isDisabled?: boolean;
    linkClassName?: string;
    iconShownOnlyOnHover?: boolean;
    onClick?: (e: PressEvent) => void;
}

// Дополнять по мере появления потребности в новых размерах, не забывая заводить класс с line-height
const iconSizeByTextSize: Record<Required<Props>['size'], IconSize> = {
    xs: '12',
    s: '12',
    m: '16',
};

export function LinkWithIcon({
    children,
    className,
    href,
    external = false,
    target = '_blank',
    size = 'm',
    color,
    isDisabled,
    inline = false,
    iconSize,
    linkClassName,
    icon: Icon = IconOpenInNewTab,
    iconShownOnlyOnHover,
    onClick,
    'data-testid': dataTestId,
}: Props) {
    const rel = `noopener${external ? ' noreferrer' : ''}`;
    const linkProps = href ? { href, rel, target: target === '' ? undefined : target } : null;

    let textStyle: string | undefined;

    switch (size) {
        case 'xs':
            textStyle = styles.text_xs;

            break;
        case 's':
            textStyle = styles.text_s;

            break;
        case 'm':
            textStyle = styles.text_m;

            break;
    }

    return (
        <Text
            size={size}
            className={cn(
                className,
                textStyle,
                iconShownOnlyOnHover && styles.iconShownOnlyOnHover,
                inline && styles.inline
            )}
        >
            <Link
                {...linkProps}
                className={linkClassName}
                color={color}
                isDisabled={isDisabled}
                onClick={onClick}
                data-testid={dataTestId}
            >
                {children}
                {NON_BREAKING_SPACE}
                <Icon size={iconSize ? iconSize : iconSizeByTextSize[size]} className={styles.icon} />
            </Link>
        </Text>
    );
}
